import { translate, resolveLocale } from '../i18n/messages';
import { buildApiUrl } from '../config/env';
import { retryOnExpiredJwt } from './authRefresh';

type TokenRefreshHandler = (expiredToken: string) => Promise<string | null | undefined>;

let tokenRefreshHandler: TokenRefreshHandler | null = null;

export function configureTokenRefresh(handler: TokenRefreshHandler | null) {
  tokenRefreshHandler = handler;
}

export interface PaginatedResponse<T> {
  items: T[];
  pagination: {
    page: number;
    page_size: number;
    total: number;
    total_pages: number;
  };
}

export interface OverviewResponse {
  totals: {
    applications: number;
    billing_customers: number;
    active_subscriptions: number;
    failed_deliveries: number;
  };
  provider_status: Array<{
    provider_key: string;
    enabled: boolean;
    status: string;
  }>;
  recent_events: Array<Record<string, unknown>>;
}

export interface ApplicationDomain {
  domain: string;
  environment: string;
  is_primary: boolean;
}

export interface ApplicationServiceClient {
  id: string;
  credential_prefix: string;
  secret_last_four: string;
  environment: string;
  display_name: string;
  allowed_scopes: string[];
  status: string;
  last_used_at: string | null;
  secret?: string;
}

export interface ApplicationWebhookEndpoint {
  id: string;
  environment: string;
  url: string;
  subscribed_event_types: string[];
  status: string;
  signing_secret_last_four: string | null;
}

export interface ApplicationRecord {
  id: string;
  key: string;
  display_name: string;
  description: string | null;
  status: string;
  default_environment: string | null;
  created_at: string;
  updated_at: string;
  domains: ApplicationDomain[];
  service_clients: ApplicationServiceClient[];
  webhook_endpoints: ApplicationWebhookEndpoint[];
}

export interface ProviderRecord {
  id: string;
  key: string;
  display_name: string;
  provider_type: string;
  status: string;
  config: Record<string, unknown>;
  capabilities: Array<{
    key: string;
    status: string;
    notes: string | null;
  }>;
  created_at: string;
  updated_at: string;
}

export interface ProviderCapabilitiesRecord {
  provider_key: string;
  capabilities: ProviderRecord['capabilities'];
}

export interface ProviderProductMappingRecord {
  id: string;
  billing_product_id: string;
  provider_id: string;
  provider_key: string;
  provider_product_id: string | null;
  provider_price_id: string | null;
  environment: string;
  status: string;
  provider_metadata: Record<string, unknown>;
}

export interface ProductRecord {
  id: string;
  application_id: string;
  application_key: string;
  application_display_name: string;
  key: string;
  display_name: string;
  description: string | null;
  product_type: string;
  is_public: boolean;
  sort_order: number;
  is_featured: boolean;
  feature_metadata: Record<string, unknown>;
  upgrade_product_keys: string[];
  downgrade_product_keys: string[];
  visibility: 'public' | 'internal' | 'hidden';
  status: string;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  provider_mappings: ProviderProductMappingRecord[];
  prices: PriceRecord[];
}

export interface ProviderPriceMappingRecord {
  id: string;
  billing_price_id: string;
  provider_id: string;
  provider_key: string;
  provider_product_id: string | null;
  provider_price_id: string | null;
  environment: string;
  status: string;
  provider_metadata: Record<string, unknown>;
}

export interface PriceRecord {
  id: string;
  billing_product_id: string;
  product_key: string;
  key: string;
  display_name: string;
  description: string | null;
  currency: string;
  amount: number;
  billing_interval: string;
  trial_days: number | null;
  is_default: boolean;
  compare_at_amount: number | null;
  display_badge: string | null;
  is_recommended: boolean;
  sort_order: number;
  status: string;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  provider_mappings: ProviderPriceMappingRecord[];
}

export interface CheckoutSessionRecord {
  id: string;
  application_key: string;
  billing_customer_id: string;
  billing_product_key: string;
  provider_key: string;
  provider_checkout_id: string | null;
  provider_checkout_url: string | null;
  status: string;
  expires_at: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface BillingInvoiceRecord {
  id: string;
  application_key: string;
  billing_customer_id: string;
  subscription_id: string | null;
  provider_key: string;
  provider_invoice_id: string | null;
  invoice_number: string | null;
  status: string;
  currency: string | null;
  amount_due: number | null;
  amount_paid: number | null;
  hosted_url: string | null;
  pdf_url: string | null;
  issued_at: string | null;
  due_at: string | null;
  paid_at: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface BillingTransactionRecord {
  id: string;
  application_key: string;
  billing_customer_id: string;
  subscription_id: string | null;
  provider_key: string;
  provider_transaction_id: string | null;
  transaction_type: string;
  status: string;
  currency: string | null;
  amount: number | null;
  occurred_at: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface ApplicationInput {
  key: string;
  display_name: string;
  description?: string | null;
  status: string;
  default_environment?: string | null;
  domains: ApplicationDomain[];
}

export interface ApplicationWebhookInput {
  environment: string;
  url: string;
  subscribed_event_types: string[];
  status: string;
  signing_secret?: string | null;
}

export interface ApplicationCredentialInput {
  environment: string;
  display_name: string;
  allowed_scopes: string[];
  status: string;
}

export interface ProviderProductMappingInput {
  id?: string;
  provider_key: string;
  provider_product_id?: string | null;
  provider_price_id?: string | null;
  environment: string;
  status: string;
  provider_metadata: Record<string, unknown>;
}

export interface ProviderPriceMappingInput {
  id?: string;
  provider_key: string;
  provider_product_id?: string | null;
  provider_price_id?: string | null;
  environment: string;
  status: string;
  provider_metadata: Record<string, unknown>;
}

export interface ProductInput {
  application_id: string;
  key: string;
  display_name: string;
  description?: string | null;
  product_type: string;
  is_public?: boolean;
  sort_order?: number;
  is_featured?: boolean;
  feature_metadata?: Record<string, unknown>;
  upgrade_product_keys?: string[];
  downgrade_product_keys?: string[];
  visibility?: 'public' | 'internal' | 'hidden';
  status: string;
  metadata: Record<string, unknown>;
  provider_mappings: ProviderProductMappingInput[];
}

export interface PriceInput {
  billing_product_id: string;
  key: string;
  display_name: string;
  description?: string | null;
  currency: string;
  amount: number;
  billing_interval: string;
  trial_days?: number | null;
  is_default?: boolean;
  compare_at_amount?: number | null;
  display_badge?: string | null;
  is_recommended?: boolean;
  sort_order?: number;
  status: string;
  metadata: Record<string, unknown>;
  provider_mappings: ProviderPriceMappingInput[];
}

export interface ReconciliationEventRecord {
  id: string;
  reconciliation_type: string;
  entity_type: string;
  entity_id: string;
  provider_id: string | null;
  previous_state: Record<string, unknown>;
  provider_state: Record<string, unknown>;
  reconciliation_action: string;
  result_status: string;
  error_message: string | null;
  created_at: string;
}

export interface ReplayOperationRecord {
  id: string;
  replay_scope: string;
  initiated_by_actor_id: string | null;
  initiated_by_actor_type: string | null;
  filter_criteria: Record<string, unknown>;
  replay_status: string;
  replayed_count: number;
  failed_count: number;
  created_at: string;
  completed_at: string | null;
}

export interface PortalSessionRecord {
  id: string;
  application_key: string;
  application_user_id: string;
  provider_key: string;
  portal_url: string | null;
  expires_at: string | null;
  status: string;
  provider_response: Record<string, unknown>;
  error_message: string | null;
  created_at: string;
}

export interface SubscriptionChangePreviewRecord {
  id: string;
  application_key: string;
  subscription_id: string;
  provider_key: string;
  current_product_key: string;
  target_product_key: string;
  effective_behavior: string;
  provider_support_status: string;
  is_estimate: boolean;
  warnings: string[];
  preview: Record<string, unknown>;
  created_at: string;
}

export interface ReviewCenterItem {
  id: string;
  severity: 'critical' | 'warning' | 'info';
  category: 'delivery' | 'reconciliation' | 'subscription' | 'checkout' | 'provider';
  title: string;
  description: string;
  status: string;
  action: 'replay_dead_letters' | 'rerun_reconciliation' | 'open_detail';
  related_path: string | null;
  created_at: string;
  metadata: Record<string, unknown>;
}

export interface ReviewCenterResponse {
  generated_at: string;
  totals: {
    critical: number;
    warning: number;
    info: number;
    total: number;
  };
  items: ReviewCenterItem[];
}

export interface AdminAuditLogRecord {
  id: string;
  actor_id: string | null;
  actor_type: string;
  actor_roles: string[];
  action: string;
  target_type: string;
  target_id: string | null;
  target_display_name: string | null;
  result_status: 'success' | 'failure';
  error_code: string | null;
  error_message: string | null;
  request_method: string;
  request_path: string;
  request_id: string;
  correlation_id: string;
  ip_address: string | null;
  user_agent: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface AdminAuditLogFilters {
  actor_id?: string;
  action?: string;
  target_type?: string;
  result_status?: 'success' | 'failure' | '';
  request_id?: string;
  created_from?: string;
  created_to?: string;
}

export interface CustomerTimelineItem {
  id: string;
  occurred_at: string;
  category:
    | 'checkout'
    | 'subscription'
    | 'invoice'
    | 'transaction'
    | 'event'
    | 'delivery'
    | 'reconciliation';
  title: string;
  status: string | null;
  description: string;
  related_path: string | null;
  metadata: Record<string, unknown>;
}

export interface CustomerTimelineResponse {
  customer: Record<string, unknown>;
  generated_at: string;
  items: CustomerTimelineItem[];
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly statusCode: number,
    readonly code: string,
  ) {
    super(message);
  }
}

function parseJson(raw: string) {
  if (!raw.trim()) {
    return undefined;
  }

  try {
    return JSON.parse(raw) as unknown;
  } catch {
    throw new ApiError(
      translate('billing.ui.response.was.not.valid.json'),
      502,
      'invalid_json_response',
    );
  }
}

async function requestOnce<T>(
  path: string,
  token: string,
  init?: RequestInit & { bodyJson?: unknown },
) {
  const headers: Record<string, string> = {
    authorization: `Bearer ${token}`,
    accept: 'application/json',
    'accept-language': resolveLocale(),
  };

  if (init?.bodyJson !== undefined) {
    headers['content-type'] = 'application/json';
  }

  const response = await fetch(buildApiUrl(path), {
    ...init,
    headers: {
      ...headers,
      ...(init?.headers as Record<string, string> | undefined),
    },
    body: init?.bodyJson === undefined ? init?.body : JSON.stringify(init.bodyJson),
  });

  const rawBody = await response.text();
  const payload = parseJson(rawBody) as
    | {
        error?: { code?: string; message?: string };
        code?: string;
        message?: string;
      }
    | undefined;

  if (!response.ok) {
    throw new ApiError(
      payload?.error?.message ?? payload?.message ?? response.statusText,
      response.status,
      payload?.error?.code ?? payload?.code ?? 'request_failed',
    );
  }

  if (response.status === 204 || payload === undefined) {
    return undefined as T;
  }

  return payload as T;
}

async function request<T>(
  path: string,
  token: string,
  init?: RequestInit & { bodyJson?: unknown },
) {
  return retryOnExpiredJwt({
    token,
    request: (accessToken) => requestOnce<T>(path, accessToken, init),
    refreshToken: async (expiredToken) => {
      if (!tokenRefreshHandler) {
        return null;
      }

      try {
        return await tokenRefreshHandler(expiredToken);
      } catch {
        throw new ApiError(
          translate('billing.ui.session.refresh.failed'),
          401,
          'keycloak_refresh_failed',
        );
      }
    },
  });
}

function paginatedRequest<T>(path: string, token: string, signal?: AbortSignal) {
  return request<PaginatedResponse<T>>(path, token, { signal });
}

export const billingApi = {
  getReviewCenter(token: string, signal?: AbortSignal) {
    return request<ReviewCenterResponse>('/admin/review-center', token, {
      signal,
    });
  },
  getCustomerTimeline(token: string, customerId: string, signal?: AbortSignal) {
    return request<CustomerTimelineResponse>(`/admin/customers/${customerId}/timeline`, token, {
      signal,
    });
  },
  getOverview(token: string) {
    return request<OverviewResponse>('/admin/overview', token);
  },
  getApplications(token: string, page: number, signal?: AbortSignal) {
    return paginatedRequest<ApplicationRecord>(
      `/admin/applications?page=${page}&page_size=20`,
      token,
      signal,
    );
  },
  getProviders(token: string, page: number, signal?: AbortSignal) {
    return paginatedRequest<ProviderRecord>(
      `/admin/providers?page=${page}&page_size=20`,
      token,
      signal,
    );
  },
  getProducts(token: string, page: number, signal?: AbortSignal) {
    return paginatedRequest<ProductRecord>(
      `/admin/products?page=${page}&page_size=20`,
      token,
      signal,
    );
  },
  getPrices(token: string, page: number, signal?: AbortSignal) {
    return paginatedRequest<PriceRecord>(`/admin/prices?page=${page}&page_size=20`, token, signal);
  },
  getCustomers(token: string, page: number, signal?: AbortSignal) {
    return paginatedRequest<Record<string, unknown>>(
      `/admin/customers?page=${page}&page_size=20`,
      token,
      signal,
    );
  },
  getSubscriptions(token: string, page: number, signal?: AbortSignal) {
    return paginatedRequest<Record<string, unknown>>(
      `/admin/subscriptions?page=${page}&page_size=20`,
      token,
      signal,
    );
  },
  getCheckoutSessions(token: string, page: number, signal?: AbortSignal) {
    return paginatedRequest<Record<string, unknown>>(
      `/admin/checkout-sessions?page=${page}&page_size=20`,
      token,
      signal,
    );
  },
  getInvoices(token: string, page: number, signal?: AbortSignal) {
    return paginatedRequest<Record<string, unknown>>(
      `/admin/invoices?page=${page}&page_size=20`,
      token,
      signal,
    );
  },
  getTransactions(token: string, page: number, signal?: AbortSignal) {
    return paginatedRequest<Record<string, unknown>>(
      `/admin/transactions?page=${page}&page_size=20`,
      token,
      signal,
    );
  },
  getEvents(token: string, page: number, signal?: AbortSignal) {
    return paginatedRequest<Record<string, unknown>>(
      `/admin/events?page=${page}&page_size=20`,
      token,
      signal,
    );
  },
  getDeliveries(token: string, page: number, signal?: AbortSignal) {
    return paginatedRequest<Record<string, unknown>>(
      `/admin/deliveries?page=${page}&page_size=20`,
      token,
      signal,
    );
  },
  getDelivery(token: string, deliveryId: string) {
    return request<Record<string, unknown>>(`/admin/deliveries/${deliveryId}`, token);
  },
  getReconciliationEvents(token: string, page: number, signal?: AbortSignal) {
    return paginatedRequest<Record<string, unknown>>(
      `/admin/reconciliation-events?page=${page}&page_size=20`,
      token,
      signal,
    );
  },
  getReplayOperations(token: string, page: number, signal?: AbortSignal) {
    return paginatedRequest<Record<string, unknown>>(
      `/admin/replay-operations?page=${page}&page_size=20`,
      token,
      signal,
    );
  },
  getReplayOperation(token: string, operationId: string) {
    return request<Record<string, unknown>>(`/admin/replay-operations/${operationId}`, token);
  },
  getAuditLogs(
    token: string,
    page: number,
    signal?: AbortSignal,
    filters: AdminAuditLogFilters = {},
  ) {
    const params = new URLSearchParams({
      page: String(page),
      page_size: '20',
    });
    for (const [key, value] of Object.entries(filters)) {
      if (value) {
        params.set(key, value);
      }
    }
    return paginatedRequest<AdminAuditLogRecord>(
      `/admin/audit-logs?${params.toString()}`,
      token,
      signal,
    );
  },
  getPortalSessions(token: string, page: number, signal?: AbortSignal) {
    return paginatedRequest<Record<string, unknown>>(
      `/admin/portal-sessions?page=${page}&page_size=20`,
      token,
      signal,
    );
  },
  getSubscriptionChangePreviews(token: string, page: number, signal?: AbortSignal) {
    return paginatedRequest<Record<string, unknown>>(
      `/admin/subscription-change-previews?page=${page}&page_size=20`,
      token,
      signal,
    );
  },
  rerunReconciliation(
    token: string,
    input: { provider_key?: string; reconciliation_type?: string },
  ) {
    return request<{ repaired_count: number; status: string }>(
      '/admin/reconciliation/rerun',
      token,
      {
        method: 'POST',
        bodyJson: input,
      },
    );
  },
  replayDeadLetters(
    token: string,
    input: { replay_scope: string; filter_criteria?: Record<string, unknown> },
  ) {
    return request<ReplayOperationRecord>('/admin/replay-operations', token, {
      method: 'POST',
      bodyJson: input,
    });
  },
  listApplications(token: string, signal?: AbortSignal) {
    return request<PaginatedResponse<ApplicationRecord>>(
      '/admin/applications?page=1&page_size=100',
      token,
      { signal },
    );
  },
  listProviders(token: string, signal?: AbortSignal) {
    return request<PaginatedResponse<ProviderRecord>>(
      '/admin/providers?page=1&page_size=100',
      token,
      { signal },
    );
  },
  listProviderCapabilities(token: string, providerKey?: string) {
    const suffix = providerKey ? `?provider_key=${encodeURIComponent(providerKey)}` : '';
    return request<ProviderCapabilitiesRecord[]>(`/admin/provider-capabilities${suffix}`, token);
  },
  listProducts(token: string, signal?: AbortSignal) {
    return request<PaginatedResponse<ProductRecord>>(
      '/admin/products?page=1&page_size=100',
      token,
      { signal },
    );
  },
  listPrices(token: string, signal?: AbortSignal) {
    return request<PaginatedResponse<PriceRecord>>('/admin/prices?page=1&page_size=100', token, {
      signal,
    });
  },
  getProduct(token: string, productId: string) {
    return request<ProductRecord>(`/admin/products/${productId}`, token);
  },
  getPrice(token: string, priceId: string) {
    return request<PriceRecord>(`/admin/prices/${priceId}`, token);
  },
  getCustomer(token: string, customerId: string) {
    return request<Record<string, unknown>>(`/admin/customers/${customerId}`, token);
  },
  getSubscription(token: string, subscriptionId: string) {
    return request<Record<string, unknown>>(`/admin/subscriptions/${subscriptionId}`, token);
  },
  getCheckoutSession(token: string, checkoutSessionId: string) {
    return request<CheckoutSessionRecord>(`/admin/checkout-sessions/${checkoutSessionId}`, token);
  },
  getInvoice(token: string, invoiceId: string) {
    return request<BillingInvoiceRecord>(`/admin/invoices/${invoiceId}`, token);
  },
  getTransaction(token: string, transactionId: string) {
    return request<BillingTransactionRecord>(`/admin/transactions/${transactionId}`, token);
  },
  createApplication(token: string, input: ApplicationInput) {
    return request<ApplicationRecord>('/admin/applications', token, {
      method: 'POST',
      bodyJson: input,
    });
  },
  updateApplication(token: string, applicationId: string, input: ApplicationInput) {
    return request<ApplicationRecord>(`/admin/applications/${applicationId}`, token, {
      method: 'PUT',
      bodyJson: input,
    });
  },
  deleteApplication(token: string, applicationId: string) {
    return request<void>(`/admin/applications/${applicationId}`, token, {
      method: 'DELETE',
    });
  },
  createApplicationWebhook(token: string, applicationId: string, input: ApplicationWebhookInput) {
    return request<ApplicationWebhookEndpoint>(
      `/admin/applications/${applicationId}/webhooks`,
      token,
      {
        method: 'POST',
        bodyJson: input,
      },
    );
  },
  updateApplicationWebhook(
    token: string,
    applicationId: string,
    webhookId: string,
    input: ApplicationWebhookInput,
  ) {
    return request<ApplicationWebhookEndpoint>(
      `/admin/applications/${applicationId}/webhooks/${webhookId}`,
      token,
      {
        method: 'PUT',
        bodyJson: input,
      },
    );
  },
  deleteApplicationWebhook(token: string, applicationId: string, webhookId: string) {
    return request<void>(`/admin/applications/${applicationId}/webhooks/${webhookId}`, token, {
      method: 'DELETE',
    });
  },
  createApplicationCredential(
    token: string,
    applicationId: string,
    input: ApplicationCredentialInput,
  ) {
    return request<ApplicationServiceClient>(
      `/admin/applications/${applicationId}/credentials`,
      token,
      {
        method: 'POST',
        bodyJson: input,
      },
    );
  },
  updateApplicationCredential(
    token: string,
    applicationId: string,
    credentialId: string,
    input: ApplicationCredentialInput,
  ) {
    return request<ApplicationServiceClient>(
      `/admin/applications/${applicationId}/credentials/${credentialId}`,
      token,
      {
        method: 'PUT',
        bodyJson: input,
      },
    );
  },
  deleteApplicationCredential(token: string, applicationId: string, credentialId: string) {
    return request<void>(
      `/admin/applications/${applicationId}/credentials/${credentialId}`,
      token,
      {
        method: 'DELETE',
      },
    );
  },
  createProduct(token: string, input: ProductInput) {
    return request<ProductRecord>('/admin/products', token, {
      method: 'POST',
      bodyJson: input,
    });
  },
  updateProduct(token: string, productId: string, input: ProductInput) {
    return request<ProductRecord>(`/admin/products/${productId}`, token, {
      method: 'PUT',
      bodyJson: input,
    });
  },
  deleteProduct(token: string, productId: string) {
    return request<void>(`/admin/products/${productId}`, token, {
      method: 'DELETE',
    });
  },
  createPrice(token: string, input: PriceInput) {
    return request<PriceRecord>('/admin/prices', token, {
      method: 'POST',
      bodyJson: input,
    });
  },
  updatePrice(token: string, priceId: string, input: PriceInput) {
    return request<PriceRecord>(`/admin/prices/${priceId}`, token, {
      method: 'PUT',
      bodyJson: input,
    });
  },
  deletePrice(token: string, priceId: string) {
    return request<void>(`/admin/prices/${priceId}`, token, {
      method: 'DELETE',
    });
  },
};
